import { TPSScores } from '../types/tps.types';

export interface KeirseyDetail {
  temperament: 'guardian' | 'artisan' | 'idealist' | 'rational';
  temperamentName: string;
  pattern: string;
  role: string;
  confidence: number;
  description: string;
  coreNeeds: string[];
  selfImage: string;
  valuedTraits: string[];
}

// Keirsey Temperaments (derived from MBTI patterns)
const KEIRSEY_MAPPINGS = {
  guardian: {
    // SJ types: ESTJ, ISTJ, ESFJ, ISFJ
    mbtiPatterns: ['ESTJ', 'ISTJ', 'ESFJ', 'ISFJ'],
    roles: { 'ESTJ': 'Supervisor', 'ISTJ': 'Inspector', 'ESFJ': 'Provider', 'ISFJ': 'Protector' },
    description: 'Dependable, helpful, and hardworking',
    coreNeeds: ['Membership', 'Responsibility', 'Duty'],
    selfImage: 'Reliable, Respectable, Dependable',
    valuedTraits: ['Dependability', 'Beneficence', 'Respectability']
  },
  artisan: {
    // SP types: ESTP, ISTP, ESFP, ISFP
    mbtiPatterns: ['ESTP', 'ISTP', 'ESFP', 'ISFP'],
    roles: { 'ESTP': 'Promoter', 'ISTP': 'Crafter', 'ESFP': 'Performer', 'ISFP': 'Composer' },
    description: 'Adaptable, artistic, and adventurous',
    coreNeeds: ['Freedom', 'Skill', 'Action'],
    selfImage: 'Artistic, Audacious, Adaptable',
    valuedTraits: ['Grace', 'Boldness', 'Adaptability']
  },
  idealist: {
    // NF types: ENFJ, INFJ, ENFP, INFP
    mbtiPatterns: ['ENFJ', 'INFJ', 'ENFP', 'INFP'],
    roles: { 'ENFJ': 'Teacher', 'INFJ': 'Counselor', 'ENFP': 'Champion', 'INFP': 'Healer' },
    description: 'Empathetic, benevolent, and authentic',
    coreNeeds: ['Identity', 'Meaning', 'Significance'],
    selfImage: 'Empathic, Benevolent, Authentic',
    valuedTraits: ['Empathy', 'Authenticity', 'Benevolence']
  },
  rational: {
    // NT types: ENTJ, INTJ, ENTP, INTP
    mbtiPatterns: ['ENTJ', 'INTJ', 'ENTP', 'INTP'],
    roles: { 'ENTJ': 'Fieldmarshal', 'INTJ': 'Mastermind', 'ENTP': 'Inventor', 'INTP': 'Architect' },
    description: 'Analytical, strategic, and competent',
    coreNeeds: ['Knowledge', 'Competence', 'Achievement'],
    selfImage: 'Ingenious, Autonomous, Resolute',
    valuedTraits: ['Reason', 'Willpower', 'Ingenuity']
  }
};

// Derive Keirsey from MBTI type
export function calculateKeirsey(mbtiType: string): KeirseyDetail {
  // Callers have historically passed the whole trait-score object here, which
  // threw and aborted the entire profile — 16 stored persona rows died this way.
  // A non-string input is a caller bug, not a reason to lose the profile.
  const upperType = typeof mbtiType === 'string' ? mbtiType.toUpperCase() : '';
  
  let temperament: keyof typeof KEIRSEY_MAPPINGS = 'guardian';
  let confidence = 85;

  // Find matching temperament based on MBTI pattern
  for (const [temp, config] of Object.entries(KEIRSEY_MAPPINGS)) {
    if (config.mbtiPatterns.includes(upperType)) {
      temperament = temp as keyof typeof KEIRSEY_MAPPINGS;
      confidence = 95; // High confidence when MBTI matches directly
      break;
    }
  }

  const tempConfig = KEIRSEY_MAPPINGS[temperament];
  const role = tempConfig.roles[upperType as keyof typeof tempConfig.roles] || 'Unknown';

  return {
    temperament,
    temperamentName: temperament.charAt(0).toUpperCase() + temperament.slice(1),
    pattern: upperType.slice(1, 3), // SJ, SP, NF, NT
    role,
    confidence,
    description: tempConfig.description,
    coreNeeds: tempConfig.coreNeeds,
    selfImage: tempConfig.selfImage,
    valuedTraits: tempConfig.valuedTraits
  };
}

// Calculate Keirsey from trait scores (when MBTI not available)
export function calculateKeirseyFromTraits(scores: TPSScores): KeirseyDetail {
  // Simplified calculation using key trait patterns
  const sensingScore = (scores['Physical'] || 5) + (scores['Structured'] || 5) + (scores['Pragmatic'] || 5);
  const intuitionScore = (scores['Intuitive'] || 5) + (scores['Universal'] || 5) + (scores['Self-Aware'] || 5);
  const thinkingScore = (scores['Analytical'] || 5) + (scores['Stoic'] || 5) + (scores['Pragmatic'] || 5);
  const feelingScore = (scores['Responsive'] || 5) + (scores['Diplomatic'] || 5) + (scores['Social'] || 5); // TPS-EXEMPT: Social=processing.reality.social
  const judgingScore = (scores['Structured'] || 5) + (scores['Lawful'] || 5) + (scores['Self-Mastery'] || 5);
  const perceivingScore = (scores['Dynamic'] || 5) + (scores['Ambivalent'] || 5) + (scores['Varied'] || 5);

  const isS = sensingScore > intuitionScore;
  const isN = !isS;
  const isT = thinkingScore > feelingScore;
  const isF = !isT;
  const isJ = judgingScore > perceivingScore;
  const isP = !isJ;

  let temperament: keyof typeof KEIRSEY_MAPPINGS;
  
  if (isS && isJ) temperament = 'guardian';
  else if (isS && isP) temperament = 'artisan';
  else if (isN && isF) temperament = 'idealist';
  else temperament = 'rational';

  const tempConfig = KEIRSEY_MAPPINGS[temperament];

  return {
    temperament,
    temperamentName: temperament.charAt(0).toUpperCase() + temperament.slice(1),
    pattern: `${isS ? 'S' : 'N'}${temperament === 'guardian' || temperament === 'artisan' ? (isJ ? 'J' : 'P') : (isT ? 'T' : 'F')}`,
    role: 'Inferred',
    confidence: 70,
    description: tempConfig.description,
    coreNeeds: tempConfig.coreNeeds,
    selfImage: tempConfig.selfImage,
    valuedTraits: tempConfig.valuedTraits
  };
}

// Keirsey colors
export const KEIRSEY_COLORS: Record<string, string> = {
  guardian: 'hsl(45, 80%, 50%)', // Gold
  artisan: 'hsl(30, 90%, 55%)', // Orange
  idealist: 'hsl(210, 80%, 50%)', // Blue
  rational: 'hsl(140, 60%, 40%)' // Green
};
